/* eslint-disable */
import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";

function num(v) { return v == null ? "—" : Number(v).toLocaleString(); }
function pct(v) { return v == null ? "—" : `${Math.round(v * 100)}%`; }
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
}

function Kpi({ label, value, hint, testid }) {
  return (
    <div className="m-beta-kpi" data-testid={testid}>
      <div className="m-beta-kpi-val">{value}</div>
      <div className="m-beta-kpi-label">{label}</div>
      {hint != null && <div className="m-beta-kpi-hint">{hint}</div>}
    </div>
  );
}

export default function BetaDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sept, setSept] = useState(null);

  useEffect(() => {
    api.get("/admin/beta")
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || e.message));
    // Session 60.23 · September Events KPIs — extends this dashboard rather
    // than shipping a disconnected page per CTO answer #3.
    api.get("/admin/events/september-kpis")
      .then(r => setSept(r.data))
      .catch(() => setSept(null));
  }, []);

  if (err) return <AppShell><div className="m-empty" data-testid="beta-dash-err">{err}</div></AppShell>;
  if (!data) return <AppShell><div className="m-loading-inline">Loading beta dashboard…</div></AppShell>;

  const g = data.growth;
  const a = data.activation;
  const v = data.validation;
  const d = data.demand;
  const utm = data.referral_sources || [];

  return (
    <AppShell>
      <div className="m-page-pad" data-testid="beta-dashboard">
        <span className="m-eyebrow">CEO view · beta</span>
        <h1 className="m-h1">Beta <em>dashboard</em>.</h1>
        <p className="m-muted" style={{maxWidth: 640, marginTop: 4}}>
          Every metric that matters between now and August 19. Anything not on this page is deprioritized.
        </p>

        <section className="m-beta-section" data-testid="beta-growth">
          <span className="m-eyebrow" style={{color: "var(--gold)"}}>Growth</span>
          <h2 className="m-beta-h2">Members joining.</h2>
          <div className="m-beta-kpi-row">
            <Kpi label="Founding Members" value={num(g.founding_members)} testid="kpi-founding" />
            <Kpi label="Beta Testers"     value={num(g.beta_testers)}     testid="kpi-beta"  />
            <Kpi label="Onboarded" value={num(g.onboarded_count)} hint={g.onboarding_rate == null ? undefined : `${pct(g.onboarding_rate)} completed the welcome tour`} testid="kpi-onboarded" />
            <Kpi label="New · 7d"  value={`+${num(g.new_7d)}`}  testid="kpi-new-7d" />
            <Kpi label="New · 30d" value={`+${num(g.new_30d)}`} testid="kpi-new-30d" />
          </div>
        </section>

        <section className="m-beta-section" data-testid="beta-activation">
          <span className="m-eyebrow" style={{color: "var(--gold)"}}>Activation</span>
          <h2 className="m-beta-h2">Workspace interest.</h2>
          <div className="m-beta-kpi-row">
            <Kpi label="Workspace clicks · all time" value={num(a.workspace_clicks_total)} testid="kpi-clicks-total" />
            <Kpi label="External clicks · 30d"       value={num(a.workspace_clicks_30d)}   testid="kpi-clicks-30d" />
            <Kpi label="External clicks · 7d"        value={num(a.workspace_clicks_7d)}    testid="kpi-clicks-7d" />
          </div>
          {a.top_workspaces && a.top_workspaces.length > 0 && (
            <div className="m-beta-table-wrap">
              <div className="m-beta-sub">Top-performing workspaces</div>
              <table className="m-utm-table" data-testid="kpi-top-workspaces">
                <thead><tr><th>Workspace</th><th style={{textAlign:"right"}}>Clicks</th><th>Last</th></tr></thead>
                <tbody>
                  {a.top_workspaces.map(w => (
                    <tr key={w.workspace_slug}>
                      <td>{w.workspace_slug}</td>
                      <td style={{textAlign:"right"}}><strong>{w.clicks}</strong></td>
                      <td className="m-muted small">{fmtDate(w.last_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="m-beta-section" data-testid="beta-validation">
          <span className="m-eyebrow" style={{color: "var(--gold)"}}>Validation</span>
          <h2 className="m-beta-h2">Are members giving feedback?</h2>
          <div className="m-beta-kpi-row">
            <Kpi label="Completed reviews"      value={num(v.reviews_total)}         testid="kpi-reviews" />
            <Kpi label="Reviews · 30d"           value={num(v.reviews_30d)}           testid="kpi-reviews-30d" />
            <Kpi label="Quick feedback events"   value={num(v.feedback_events_total)} testid="kpi-feedback" />
          </div>
        </section>

        <div className="m-beta-two">
          <section className="m-beta-section" data-testid="beta-demand-cities">
            <span className="m-eyebrow" style={{color: "var(--gold)"}}>Demand · geography</span>
            <h2 className="m-beta-h2">Top-performing cities.</h2>
            {(d.top_cities || []).length === 0 ? (
              <div className="m-muted small">No signups yet.</div>
            ) : (
              <table className="m-utm-table">
                <thead><tr><th>City</th><th style={{textAlign:"right"}}>Members</th></tr></thead>
                <tbody>
                  {d.top_cities.map(c => (
                    <tr key={c.city}><td>{c.city}</td><td style={{textAlign:"right"}}><strong>{c.members}</strong></td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="m-beta-section" data-testid="beta-referral-sources">
            <span className="m-eyebrow" style={{color: "var(--gold)"}}>Referral sources</span>
            <h2 className="m-beta-h2">Where clicks come from.</h2>
            {utm.length === 0 ? (
              <div className="m-muted small">No UTM traffic yet.</div>
            ) : (
              <table className="m-utm-table">
                <thead><tr><th>Campaign / source</th><th style={{textAlign:"right"}}>Clicks</th></tr></thead>
                <tbody>
                  {utm.map(r => (
                    <tr key={r.campaign}><td>{r.campaign}</td><td style={{textAlign:"right"}}><strong>{r.clicks}</strong></td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>

        <section className="m-beta-section" data-testid="beta-demand-partners">
          <span className="m-eyebrow" style={{color: "var(--gold)"}}>Demand · partner requests</span>
          <h2 className="m-beta-h2">Workspaces members want as partners.</h2>
          <p className="m-muted" style={{marginTop: -4, marginBottom: 12, maxWidth: 620}}>
            Use these counts as the exact sales quote in your outreach: <em style={{color:"var(--gold)"}}>&ldquo;N professionals in your area have requested your space be added as an official Métier partner.&rdquo;</em>
          </p>
          {(d.partner_requests || []).length === 0 ? (
            <div className="m-muted small">No partner requests yet, the button lives on `/nh`, `/ma`, `/dmv`.</div>
          ) : (
            <table className="m-utm-table" data-testid="kpi-partner-requests">
              <thead><tr><th>Workspace</th><th>City</th><th style={{textAlign:"right"}}>Requests</th></tr></thead>
              <tbody>
                {d.partner_requests.map(r => (
                  <tr key={r.workspace_slug}>
                    <td><strong>{r.workspace_name}</strong></td>
                    <td className="m-muted small">{r.workspace_city || "—"}</td>
                    <td style={{textAlign:"right"}}><strong style={{color:"var(--gold)"}}>{r.count}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {sept && (
          <section className="m-beta-section" data-testid="beta-september-events">
            <span className="m-eyebrow" style={{color: "var(--gold)"}}>September Events Growth Sprint</span>
            <h2 className="m-beta-h2">Publish 25 · Convert 25 RSVPs.</h2>
            <div className="m-beta-kpi-row">
              <Kpi label={`Published events · ${sept.progress.published_events.value} / ${sept.progress.published_events.target}`}
                   value={`${sept.progress.published_events.pct}%`} testid="sept-kpi-events" />
              <Kpi label={`Métier RSVPs · ${sept.progress.rsvps.value} / ${sept.progress.rsvps.target}`}
                   value={`${sept.progress.rsvps.pct}%`} testid="sept-kpi-rsvps" />
              <Kpi label="Unique Participants" value={num(sept.metrics.unique_event_participants)} testid="sept-kpi-unique" />
              <Kpi label="Unique Viewers" value={num(sept.metrics.unique_event_viewers)} hint={`${num(sept.metrics.total_event_views)} total views`} testid="sept-kpi-viewers" />
              <Kpi label="RSVP conversion" value={`${sept.metrics.rsvp_conversion_pct}%`} hint={`target ${sept.targets.conversion_pct}%`} testid="sept-kpi-conversion" />
              <Kpi label={`Partner events · ${sept.progress.partner_events.value} / ${sept.progress.partner_events.target}`}
                   value={`${sept.progress.partner_events.pct}%`} testid="sept-kpi-partner" />
              <Kpi label="External clicks" value={num(sept.metrics.external_registration_clicks)} testid="sept-kpi-clicks" />
              <Kpi label={`Event-attributed signups · ${sept.progress.attributed_signups.value} / ${sept.progress.attributed_signups.target}`}
                   value={`${sept.progress.attributed_signups.pct}%`} testid="sept-kpi-attrib" />
              <Kpi label="Event → Commons contributors" value={num(sept.metrics.event_commons_contributors)} testid="sept-kpi-commons" />
              <Kpi label="Shares started" value={num(sept.metrics.share_started)} testid="sept-kpi-shares" />
              <Kpi label="Calendar adds" value={num(sept.metrics.calendar_adds)} testid="sept-kpi-calendar" />
            </div>
          </section>
        )}

        <div className="m-muted small" style={{marginTop: 24}}>Generated at {new Date(data.generated_at).toLocaleString()}</div>
      </div>
    </AppShell>
  );
}
